import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { Box } from "@mui/material";
import Flex from "@/components/Flex";
import StatusChip from "@/components/StatusChip";
import StyledDialog from "@/components/StyledDialog";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  FINALIZE_CHECK_STATE,
  FINALIZE_SESSION_LABEL,
} from "../group-visit-constant";
import { FinalizeCheck } from "../group-visit-types";

export type FinalizeSessionModalProps = {
  open: boolean;
  onClose: () => void;
  /** Readiness rows, in the order the dialog lists them. */
  checks: FinalizeCheck[];
  summary: string;
  /** Individual notes still outstanding; drives the warning and the action. */
  hasIncompleteNotes: boolean;
  onConfirm: () => void;
  isSubmitting?: boolean;
};

const ICON_SIZE = 18;

/**
 * Readiness check before a session is finalized — what is done, what is not,
 * and what finalizing now would leave behind.
 *
 * @author Rajkumar Rathod
 */
const FinalizeSessionModal = ({
  open,
  onClose,
  checks,
  summary,
  hasIncompleteNotes,
  onConfirm,
  isSubmitting,
}: FinalizeSessionModalProps) => (
  <StyledDialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    dialogTitle={
      <Flex flexDirection="column" gap={0.5}>
        <Text
          {...CHART_TYPOGRAPHY.body3}
          fontWeight={CHART_TYPOGRAPHY.weight.semiBold}
          c={CHART_COLORS.textDarker}
        >
          {FINALIZE_SESSION_LABEL.title}
        </Text>
        <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
          {summary}
        </Text>
      </Flex>
    }
    dialogAction={
      <>
        <StyledButtonV2
          label={FINALIZE_SESSION_LABEL.keepEditing}
          variant="outlined"
          onClick={onClose}
        />
        <StyledButtonV2
          label={FINALIZE_SESSION_LABEL.finalizeAll}
          variant="primary"
          disabled={hasIncompleteNotes || isSubmitting}
          onClick={onConfirm}
        />
      </>
    }
  >
    <Flex flexDirection="column" gap={1.5}>
      <Flex flexDirection="column" gap={1}>
        {checks.map((check) => {
          const isComplete = check.state === FINALIZE_CHECK_STATE.COMPLETE;

          return (
            <Flex
              key={check.id}
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{
                px: 1.5,
                py: 1.25,
                border: `1px solid ${CHART_COLORS.border}`,
                borderRadius: CHART_DIMENSIONS.borderRadius,
              }}
            >
              <Flex alignItems="center" gap={1}>
                {isComplete ? (
                  <CheckCircleIcon
                    sx={{
                      fontSize: ICON_SIZE,
                      color: CHART_COLORS.badgeSuccessText,
                    }}
                  />
                ) : (
                  <HourglassBottomIcon
                    sx={{
                      fontSize: ICON_SIZE,
                      color: CHART_COLORS.badgeWarningText,
                    }}
                  />
                )}

                <Text
                  {...CHART_TYPOGRAPHY.body4}
                  fontWeight={CHART_TYPOGRAPHY.weight.medium}
                  c={CHART_COLORS.textDarker}
                >
                  {check.label}
                </Text>
              </Flex>

              <StatusChip
                label={check.statusLabel}
                variant={isComplete ? "success" : "warning"}
              />
            </Flex>
          );
        })}
      </Flex>

      {hasIncompleteNotes && (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: CHART_DIMENSIONS.borderRadius,
            backgroundColor: CHART_COLORS.badgeWarningBg,
          }}
        >
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.badgeWarningText}>
            {FINALIZE_SESSION_LABEL.incompleteWarning}
          </Text>
        </Box>
      )}
    </Flex>
  </StyledDialog>
);

export default FinalizeSessionModal;
